import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageCircle, X, Send, Trash2 } from 'lucide-react';
import { useConfirmation } from '@/contexts/ConfirmationContext';
import { useFeatureFlag } from '@/contexts/FeatureFlagsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Chatbot() {
  const chatbotEnabled = useFeatureFlag('chatbot_enabled');
  const { confirm } = useConfirmation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [videos, setVideos] = useState([]);
  const [series, setSeries] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [welcomeMessage, setWelcomeMessage] = useState('👋 Salut ! Je suis Laurent Cabanne, ton coach de golf.\nPose-moi tes questions sur le golf, je suis là pour t\'aider !');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // State pour le popup vidéo
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const [selectedVideoForPopup, setSelectedVideoForPopup] = useState(null);
  
  // State pour le popup d'abonnement
  const [showSubscribePopup, setShowSubscribePopup] = useState(false);
  const [pendingVideo, setPendingVideo] = useState(null);
  
  // Vérifier si l'utilisateur est abonné
  const isUserSubscribed = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        // Vérifier si l'utilisateur a un abonnement actif
        return user.subscription_status === 'active' || user.role === 'admin';
      } catch (e) {
        return false;
      }
    }
    return false;
  };

  // Obtenir l'ID utilisateur
  const getUserId = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.id || user.email || 'anonymous';
      } catch (error) {
        return 'anonymous';
      }
    }
    return 'anonymous';
  };

  // Charger l'historique (spécifique à l'utilisateur avec expiration 24h)
  useEffect(() => {
    const userId = getUserId();
    const storageKey = `chatbot_history_${userId}`;
    const savedData = localStorage.getItem(storageKey);
    
    if (savedData) {
      try {
        const { messages: savedMessages, timestamp } = JSON.parse(savedData);
        const now = Date.now();
        const oneDay = 24 * 60 * 60 * 1000; // 24 heures en millisecondes
        
        // Vérifier si l'historique a moins de 24h
        if (now - timestamp < oneDay && Array.isArray(savedMessages) && savedMessages.length > 0) {
          setMessages(savedMessages);
        } else {
          // Historique expiré, on le supprime
          localStorage.removeItem(storageKey);
        }
      } catch (error) {
        console.error('Error loading chat history:', error);
        localStorage.removeItem(storageKey);
      }
    }
  }, []);

  // Sauvegarder l'historique (spécifique à l'utilisateur avec timestamp)
  useEffect(() => {
    if (messages.length > 0) {
      const userId = getUserId();
      const storageKey = `chatbot_history_${userId}`;
      const dataToSave = {
        messages,
        timestamp: Date.now()
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    }
  }, [messages]);

  // Charger le message d'accueil à chaque ouverture du chatbot
  useEffect(() => {
    const loadWelcomeMessage = async () => {
      try {
        const response = await axios.get(`${API}/chatbot/welcome-message`);
        if (response.data?.message) {
          setWelcomeMessage(response.data.message);
          
          // Mettre à jour le premier message s'il existe déjà dans l'historique
          setMessages(prevMessages => {
            if (prevMessages.length > 0 && prevMessages[0].role === 'assistant') {
              // Vérifier si c'est bien un message d'accueil (pas une réponse à une question)
              const firstUserMessageIndex = prevMessages.findIndex(m => m.role === 'user');
              if (firstUserMessageIndex === -1 || firstUserMessageIndex > 0) {
                // C'est le message d'accueil, on le met à jour
                return [{
                  ...prevMessages[0],
                  content: response.data.message
                }, ...prevMessages.slice(1)];
              }
            }
            return prevMessages;
          });
        }
      } catch (error) {
        console.error('Error loading welcome message:', error);
        // Garder le message par défaut en cas d'erreur
      }
    };
    
    // Recharger le message à chaque fois qu'on ouvre le chatbot
    if (isOpen) {
      loadWelcomeMessage();
    }
  }, [isOpen]);

  // Charger les programmes, vidéos, séries et podcasts
  useEffect(() => {
    const loadContent = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const [programsRes, seriesRes, podcastsRes] = await Promise.all([
          axios.get(`${API}/programs`, { headers }),
          axios.get(`${API}/video-series`, { headers }),
          axios.get(`${API}/podcasts`, { headers })
        ]);
        
        // Charger les vidéos avec l'endpoint chatbot (inclut vidéos des séries)
        let videosData = [];
        try {
          const allVideosRes = await axios.get(`${API}/chatbot/videos`, { headers });
          videosData = allVideosRes.data || [];
        } catch {
          // Fallback: endpoint public
          try {
            const videosRes = await axios.get(`${API}/videos`, { headers });
            videosData = videosRes.data || [];
          } catch { /* ignore */ }
        }
        
        setPrograms(programsRes.data || []);
        setVideos(videosData);
        setSeries(seriesRes.data || []);
        setPodcasts(podcastsRes.data || []);
      } catch (error) {
        console.error('Error loading content:', error);
      }
    };
    loadContent();
  }, []);

  // Écouter les événements pour envoyer un message automatiquement (depuis Practice, etc.)
  useEffect(() => {
    const handlePrefillMessage = (event) => {
      const { message } = event.detail;
      if (message) {
        setIsOpen(true);
        // Attendre que le chatbot soit ouvert puis envoyer le message
        setTimeout(() => {
          // Ajouter le message de l'utilisateur
          const userMessage = { role: 'user', content: message };
          setMessages(prev => [...prev, userMessage]);
          setLoading(true);
          setIsTyping(true);
          
          // Envoyer au backend
          const sendMessage = async () => {
            try {
              const token = localStorage.getItem('session_token');
              const response = await axios.post(`${API}/chat`, 
                { message, history: [] },
                { headers: token ? { Authorization: `Bearer ${token}` } : {} }
              );
              
              setIsTyping(false);
              const assistantMessage = { role: 'assistant', content: response.data.response };
              setMessages(prev => [...prev, assistantMessage]);
            } catch (error) {
              console.error('Error sending message:', error);
              setIsTyping(false);
              const errorMessage = { role: 'assistant', content: "Désolé, une erreur s'est produite. Réessaie plus tard." };
              setMessages(prev => [...prev, errorMessage]);
            } finally {
              setLoading(false);
            }
          };
          sendMessage();
        }, 300);
      }
    };

    window.addEventListener('chatbot-prefill', handlePrefillMessage);
    return () => {
      window.removeEventListener('chatbot-prefill', handlePrefillMessage);
    };
  }, []);

  // Mesurer la hauteur du header pour positionner le chatbot en dessous
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    if (!isOpen) return;
    
    const measure = () => {
      const header = document.querySelector('[data-testid="app-header"], .sticky.top-0');
      if (header) {
        const h = header.getBoundingClientRect().bottom + 4;
        setHeaderHeight(h);
      } else {
        setHeaderHeight(100);
      }
    };
    
    // Mesurer immédiatement + après un délai pour le rendu PWA
    measure();
    const t = setTimeout(measure, 200);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Bloquer le scroll du body quand le chatbot est ouvert
  useEffect(() => {
    if (isOpen) {
      // Bloquer le scroll de la page derrière
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      
      return () => {
        // Restaurer le scroll quand on ferme
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: welcomeMessage
      }]);
    }
    
    if (isOpen) {
      // Scroll initial quand le chat s'ouvre
      setTimeout(scrollToBottom, 100);
      setTimeout(scrollToBottom, 300);
      
      const pendingMessage = localStorage.getItem('chatbot_pending_message');
      if (pendingMessage) {
        localStorage.removeItem('chatbot_pending_message');
        setInput(pendingMessage);
        setTimeout(() => {
          if (pendingMessage) {
            handleSend(pendingMessage);
          }
        }, 500);
      }
    }
  }, [isOpen]);

  // Auto-scroll
  useEffect(() => {
    if (isOpen) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, isTyping, isOpen]);

  // Référence pour la div des messages
  const messagesContainerRef = useRef(null);

  // Fonction pour forcer le scroll en bas
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Détecter l'ouverture du clavier et scroller
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      // Multiple tentatives de scroll avec différents délais
      setTimeout(scrollToBottom, 100);
      setTimeout(scrollToBottom, 300);
      setTimeout(scrollToBottom, 500);
    };

    // Écouter les changements de taille (clavier)
    window.addEventListener('resize', handleResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      window.visualViewport.addEventListener('scroll', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
      }
    };
  }, [isOpen]);

  const handleSend = async (messageOverride = null) => {
    const messageToSend = messageOverride || input.trim();
    if (!messageToSend || loading) return;

    const userMessage = messageToSend;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    setIsTyping(true);

    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/chat`,
        { message: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setIsTyping(false);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chatbot error:', error);
      setIsTyping(false);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Désolé, j\'ai rencontré une erreur. Réessaie !' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    if (inputRef.current) {
      inputRef.current.blur();
    }
    
    setTimeout(async () => {
      const confirmed = await confirm('Voulez-vous vraiment effacer tout l\'historique de conversation ?');
      if (confirmed) {
        const userId = getUserId();
        const storageKey = `chatbot_history_${userId}`;
        localStorage.removeItem(storageKey);
        setMessages([{
          role: 'assistant',
          content: welcomeMessage
        }]);
      }
    }, 100);
  };

  const parseMessageWithLinks = (content) => {
    // Regex pour différents types de contenu
    const regexProgram1 = /\[Programme:\s*([^\]]+)\]/gi;
    const regexProgram2 = /programme\s+[«"]([^»"]+)[»"]/gi;
    const regexProgram3 = /\[([^\]]+)\]\s*!/g;
    const regexVideo = /\[Vidéo:\s*([^\]]+)\]|\[Video:\s*([^\]]+)\]|vidéo\s+[«"]([^»"]+)[»"]/gi;
    const regexSeries = /\[Série:\s*([^\]]+)\]|\[Serie:\s*([^\]]+)\]|série\s+[«"]([^»"]+)[»"]/gi;
    const regexPodcast = /\[Podcast:\s*([^\]]+)\]|podcast\s+[«"]([^»"]+)[»"]/gi;
    
    const parts = [];
    const allMatches = [];
    
    // Collecter les matches pour les programmes
    let match;
    while ((match = regexProgram1.exec(content)) !== null) {
      allMatches.push({ match, titleIndex: 1, type: 'program' });
    }
    while ((match = regexProgram2.exec(content)) !== null) {
      allMatches.push({ match, titleIndex: 1, type: 'program' });
    }
    while ((match = regexProgram3.exec(content)) !== null) {
      allMatches.push({ match, titleIndex: 1, type: 'program' });
    }
    
    // Collecter les matches pour les vidéos
    while ((match = regexVideo.exec(content)) !== null) {
      const titleIndex = match[1] ? 1 : match[2] ? 2 : 3;
      allMatches.push({ match, titleIndex, type: 'video' });
    }
    
    // Collecter les matches pour les séries
    while ((match = regexSeries.exec(content)) !== null) {
      const titleIndex = match[1] ? 1 : match[2] ? 2 : 3;
      allMatches.push({ match, titleIndex, type: 'series' });
    }
    
    // Collecter les matches pour les podcasts
    while ((match = regexPodcast.exec(content)) !== null) {
      const titleIndex = match[1] ? 1 : 2;
      allMatches.push({ match, titleIndex, type: 'podcast' });
    }
    
    allMatches.sort((a, b) => a.match.index - b.match.index);
    
    let lastIndex = 0;
    for (const { match, titleIndex, type } of allMatches) {
      if (match.index >= lastIndex) {
        if (match.index > lastIndex) {
          parts.push({ type: 'text', content: content.substring(lastIndex, match.index) });
        }
        
        const itemTitle = match[titleIndex].trim();
        let foundItem = null;
        let linkType = type;
        let itemId = null;
        
        // Chercher dans le bon type de contenu
        if (type === 'program') {
          foundItem = programs.find(p => 
            p.title.toLowerCase() === itemTitle.toLowerCase() ||
            p.title.toLowerCase().includes(itemTitle.toLowerCase()) ||
            itemTitle.toLowerCase().includes(p.title.toLowerCase())
          );
          if (foundItem) itemId = foundItem.id;
        } else if (type === 'video') {
          foundItem = videos.find(v => 
            v.title.toLowerCase() === itemTitle.toLowerCase() ||
            v.title.toLowerCase().includes(itemTitle.toLowerCase()) ||
            itemTitle.toLowerCase().includes(v.title.toLowerCase())
          );
          if (foundItem) itemId = foundItem.id;
        } else if (type === 'series') {
          foundItem = series.find(s => 
            s.title.toLowerCase() === itemTitle.toLowerCase() ||
            s.title.toLowerCase().includes(itemTitle.toLowerCase()) ||
            itemTitle.toLowerCase().includes(s.title.toLowerCase())
          );
          if (foundItem) itemId = foundItem.id;
        } else if (type === 'podcast') {
          foundItem = podcasts.find(p => 
            p.title.toLowerCase() === itemTitle.toLowerCase() ||
            p.title.toLowerCase().includes(itemTitle.toLowerCase()) ||
            itemTitle.toLowerCase().includes(p.title.toLowerCase())
          );
          if (foundItem) itemId = foundItem.id;
        }

        if (foundItem && itemId) {
          parts.push({ type: 'link', content: itemTitle, linkType, itemId });
        } else {
          // Si non trouvé, créer quand même un lien de recherche
          parts.push({ type: 'link', content: itemTitle, linkType, itemId: null, searchQuery: itemTitle });
        }
        
        lastIndex = match.index + match[0].length;
      }
    }

    if (lastIndex < content.length) {
      parts.push({ type: 'text', content: content.substring(lastIndex) });
    }

    return parts.length > 0 ? parts : [{ type: 'text', content }];
  };

  if (!chatbotEnabled) {
    return null;
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="hidden"
        data-testid="chatbot-open-btn"
        title="Chat avec Laurent"
      >
        <MessageCircle className="text-white" size={20} />
      </button>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-50 bg-white md:inset-auto md:left-auto md:right-6 md:bottom-6 md:w-96 md:h-[600px] md:max-h-[80vh] md:rounded-2xl md:shadow-2xl md:border-2 md:border-gray-200"
      data-testid="chatbot-window"
    >
      {/* Messages - commence juste en dessous du header */}
      <div 
        ref={messagesContainerRef}
        className="absolute left-0 right-0 overflow-y-auto bg-gray-50 space-y-4"
        style={{
          top: headerHeight > 0 ? `${headerHeight}px` : 'calc(env(safe-area-inset-top, 47px) + 60px)',
          bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
          paddingTop: '16px',
          paddingLeft: '16px',
          paddingRight: '16px',
          paddingBottom: '16px',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {messages.map((msg, idx) => {
          const parsedContent = msg.role === 'assistant' ? parseMessageWithLinks(msg.content) : [{ type: 'text', content: msg.content }];
          
          return (
            <div
              key={`${idx}-${msg.content.length}`}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-green-600 text-white rounded-br-none'
                    : 'bg-white text-gray-800 rounded-bl-none shadow-sm'
                }`}
              >
                <div className="text-sm whitespace-pre-wrap break-words">
                  {parsedContent.map((part, partIdx) => {
                    if (part.type === 'link') {
                      // Déterminer l'URL et l'icône selon le type
                      let url = '';
                      let icon = '';
                      
                      if (part.itemId) {
                        // Si on a l'ID exact, utiliser l'URL directe
                        if (part.linkType === 'program') {
                          url = `/program/${part.itemId}`;
                          icon = '📚';
                        } else if (part.linkType === 'video') {
                          url = `/videos?videoId=${part.itemId}`;
                          icon = '🎥';
                        } else if (part.linkType === 'series') {
                          url = `/series/${part.itemId}`;
                          icon = '📺';
                        } else if (part.linkType === 'podcast') {
                          url = `/videos?podcastId=${part.itemId}`;
                          icon = '🎙️';
                        }
                      } else if (part.searchQuery) {
                        // Si pas d'ID, créer un lien vers la page avec recherche
                        if (part.linkType === 'program') {
                          url = `/catalogue?search=${encodeURIComponent(part.searchQuery)}`;
                          icon = '📚';
                        } else if (part.linkType === 'video') {
                          url = `/videos?search=${encodeURIComponent(part.searchQuery)}`;
                          icon = '🎥';
                        } else if (part.linkType === 'series') {
                          url = `/videos?search=${encodeURIComponent(part.searchQuery)}`;
                          icon = '📺';
                        } else if (part.linkType === 'podcast') {
                          url = `/videos?search=${encodeURIComponent(part.searchQuery)}`;
                          icon = '🎙️';
                        }
                      }
                      
                      // Pour les vidéos, TOUJOURS ouvrir le popup Vimeo
                      if (part.linkType === 'video') {
                        // Chercher la vidéo par ID ou par titre
                        let video = null;
                        const searchTitle = part.content || part.searchQuery || '';
                        
                        // 1. Chercher par ID
                        if (part.itemId) {
                          video = videos.find(v => v.id === part.itemId);
                        }
                        // 2. Chercher par titre exact
                        if (!video) {
                          video = videos.find(v => v.title.toLowerCase() === searchTitle.toLowerCase());
                        }
                        // 3. Chercher par inclusion partielle
                        if (!video) {
                          video = videos.find(v => 
                            v.title.toLowerCase().includes(searchTitle.toLowerCase()) ||
                            searchTitle.toLowerCase().includes(v.title.toLowerCase())
                          );
                        }
                        // 4. Chercher par mots-clés
                        if (!video) {
                          const keywords = searchTitle.toLowerCase().split(' ').filter(w => w.length > 3);
                          video = videos.find(v => 
                            keywords.some(kw => v.title.toLowerCase().includes(kw))
                          );
                        }
                        
                        return (
                          <button
                            key={partIdx}
                            onClick={() => {
                              if (video) {
                                const isFree = video.is_free === true;
                                const subscribed = isUserSubscribed();
                                
                                if (isFree || subscribed) {
                                  setSelectedVideoForPopup(video);
                                  setShowVideoPopup(true);
                                } else {
                                  setPendingVideo(video);
                                  setShowSubscribePopup(true);
                                }
                              } else {
                                setSelectedVideoForPopup({ 
                                  title: searchTitle, 
                                  description: 'Vidéo non trouvée dans la base de données'
                                });
                                setShowVideoPopup(true);
                              }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors underline font-semibold mx-1"
                          >
                            {icon} {part.content}
                          </button>
                        );
                      }
                      
                      return (
                        <button
                          key={partIdx}
                          onClick={() => {
                            setIsOpen(false);
                            window.location.href = url;
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors underline font-semibold mx-1"
                        >
                          {icon} {part.content}
                        </button>
                      );
                    }
                    return <span key={partIdx}>{part.content}</span>;
                  })}
                </div>
              </div>
            </div>
          );
        })}
        
        {/* Show typing indicator when isTyping is true */}
        {isTyping && (
          <div className="flex justify-start">
            <div className="max-w-[85%] px-4 py-3 rounded-2xl bg-white rounded-bl-none shadow-sm">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Footer Input en bas */}
      <div 
        className="absolute bottom-0 left-0 right-0 bg-white border-t p-4"
        style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
      >
        <div className="flex gap-2 items-center">
          {/* Bouton Fermer */}
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            title="Fermer"
          >
            <X size={20} />
          </button>
          
          {/* Bouton Corbeille */}
          <button
            onClick={clearHistory}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            title="Effacer l'historique"
          >
            <Trash2 size={18} />
          </button>
          
          <Input
            ref={inputRef}
            data-testid="chatbot-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            onFocus={() => {
              // Scroller plusieurs fois quand l'utilisateur focus l'input (clavier s'ouvre)
              setTimeout(scrollToBottom, 300);
              setTimeout(scrollToBottom, 500);
              setTimeout(scrollToBottom, 700);
            }}
            placeholder="Tape ton message..."
            disabled={loading}
            className="flex-1 text-base border-gray-300 focus:border-green-500 focus:ring-green-500"
            style={{ fontSize: '16px' }}
          />
          <Button
            onClick={() => handleSend()}
            disabled={loading || !input.trim()}
            className="bg-green-600 hover:bg-green-700 px-3 py-2 flex-shrink-0"
          >
            <Send size={18} />
          </Button>
        </div>
      </div>
      
      {/* Video Popup Dialog */}
      <Dialog open={showVideoPopup} onOpenChange={setShowVideoPopup}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              🎥 {selectedVideoForPopup?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedVideoForPopup && selectedVideoForPopup.vimeo_embed_code ? (
            <div className="aspect-video w-full bg-black rounded-lg overflow-hidden">
              <div 
                className="w-full h-full"
                dangerouslySetInnerHTML={{ 
                  __html: selectedVideoForPopup.vimeo_embed_code
                    .replace(/width="\d+"/, 'width="100%"')
                    .replace(/height="\d+"/, 'height="100%"')
                }}
              />
            </div>
          ) : selectedVideoForPopup && selectedVideoForPopup.video_url ? (
            <div className="aspect-video w-full bg-black rounded-lg overflow-hidden">
              {/* Extraire l'ID Vimeo de l'URL et créer un iframe */}
              {(() => {
                const vimeoMatch = selectedVideoForPopup.video_url.match(/vimeo\.com\/(\d+)/);
                if (vimeoMatch) {
                  return (
                    <iframe
                      src={`https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title={selectedVideoForPopup.title}
                    />
                  );
                }
                return (
                  <a 
                    href={selectedVideoForPopup.video_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center h-full text-white bg-gray-800 hover:bg-gray-700"
                  >
                    🎬 Cliquez pour ouvrir la vidéo
                  </a>
                );
              })()}
            </div>
          ) : (
            <div className="aspect-video w-full bg-gray-100 rounded-lg flex items-center justify-center">
              <p className="text-gray-500">Lecteur vidéo non disponible</p>
            </div>
          )}
          {selectedVideoForPopup?.description && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">{selectedVideoForPopup.description}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Subscribe Popup Dialog - Pour les vidéos payantes quand l'utilisateur n'est pas abonné */}
      <Dialog open={showSubscribePopup} onOpenChange={setShowSubscribePopup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              🔒 Contenu réservé aux abonnés
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="text-5xl mb-4">🎬</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {pendingVideo?.title}
              </h3>
              <p className="text-gray-600 text-sm">
                Cette vidéo est réservée aux membres abonnés CabGolf.
              </p>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">✨ Avantages de l'abonnement</h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Accès à toutes les vidéos de coaching</li>
                <li>• Programmes d'entraînement personnalisés</li>
                <li>• Conseils de Laurent Cabanne</li>
                <li>• Nouvelles vidéos chaque semaine</li>
              </ul>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSubscribePopup(false);
                  setIsOpen(false);
                  window.location.href = '/subscription-plans';
                }}
                className="flex-1 font-semibold py-3 px-4 rounded-lg transition-colors text-white"
                style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F4E4BA 50%, #D4AF37 100%)' }}
              >
                👑 S'abonner
              </button>
              <button
                onClick={() => setShowSubscribePopup(false)}
                className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                Plus tard
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
